import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, Output, ViewChild, type OnChanges, type OnDestroy, type OnInit, type SimpleChanges } from '@angular/core';
import { StandardDropdownsService } from '@zaoblako/web/core/services/standard-dropdowns.service';
import type { FiltersUiVersion } from '@zaoblako/web/core/types/filters-ui-version.type';
import { SingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/single-select/single-select.component';
import { STANDARD_DROPDOWN_VIRTUAL_ITEM_SIZE, StandardDropdownEngine } from '@zaoblako/web/shared/dumbs/dropdowns/standard-dropdown/standard-dropdown.engine';
import { StandardDropdownItemComponent } from '@zaoblako/web/shared/dumbs/dropdowns/standard-dropdown/standard-dropdown-item/standard-dropdown-item.component';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';
import { StandardDropdownItemPipe } from '@zaoblako/web/shared/pipes/standard-dropdown-item.pipe';
import type { StandardDropdownDescriptorInterface } from '@zaoblako/shared/common/dtos/standard-dropdown/standard-dropdown-descriptor.dto';

import { isNil } from 'lodash';
import { SkeletonModule } from 'primeng/skeleton';

const PANEL_OPEN_DELAY = 100;
const PANEL_SCROLL_BIND_ATTEMPTS = 8;

@Component({
  selector: 'crm-standard-single-select',
  standalone: true,
  templateUrl: './standard-single-select.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    SingleSelectComponent,
    SpinnerComponent,
    StandardDropdownItemComponent,
    StandardDropdownItemPipe,
    SkeletonModule
  ]
})
export class StandardSingleSelectComponent<T extends Record<string, any> = Record<string, any>> implements OnInit, OnChanges, OnDestroy {
  @Input() public customDescriptor?: StandardDropdownDescriptorInterface;
  @Input() public descriptorApiUrl?: string;
  @Input() public noCacheApiDescriptor = false;
  @Input() public isError = false;
  @Input() public class?: string;
  @Input() public styleClass?: string;
  @Input() public itemStyleClass?: string;
  @Input() public disabled?: boolean;
  @Input() public appendTo?: string;
  @Input() public showClear = true;
  @Input() public labelInput = false;
  @Input() public labelInputText = '';
  @Input() public placeholder = '\u00A0';
  @Input() public filtersUiVersion: FiltersUiVersion = 'v2';
  @Input() public virtualScroll = false;
  @Input() public virtualScrollItemSize = STANDARD_DROPDOWN_VIRTUAL_ITEM_SIZE;
  @Input() public additionalApiParams?: Record<string, any>;

  @Output() public selectedValueChange = new EventEmitter<T>();
  @Output() public selectedKeyChange = new EventEmitter<string | number | null>();

  @ViewChild('singleSelectComponent') public singleSelectComponent?: SingleSelectComponent;

  public appliedValue?: T;
  public filterValue = '';
  public readonly engine: StandardDropdownEngine<T>;

  private panelScrollTimer?: ReturnType<typeof setTimeout>;

  public constructor(
    private readonly changeDetectorRef: ChangeDetectorRef,
    standardDropdownsService: StandardDropdownsService
  ) {
    this.engine = new StandardDropdownEngine<T>(standardDropdownsService, () => this.changeDetectorRef.markForCheck());
  }

  @Input() public set selectedValue(value: T | null | undefined) {
    if (isNil(value)) {
      this.appliedValue = value ?? undefined;
      return;
    }
    this.appliedValue = value;
    if (this.engine.ensurePresent([value])) {
      this.changeDetectorRef.markForCheck();
    }
    this.engine.preloadMissing([value]);
  }

  @Input() public set selectedKey(key: string | number | null | undefined) {
    this.engine.setKeys(isNil(key) ? (key === undefined ? undefined : []) : [key]);
  }

  public ngOnInit(): void {
    this.engine.onPageReplaced = () => {
      if (!isNil(this.appliedValue)) {
        this.engine.ensurePresent([this.appliedValue]);
        this.engine.preloadMissing([this.appliedValue]);
      }
    };
    this.engine.onKeysResolved = (items) => {
      const next = items[0];
      if (this.engine.keyOf(this.appliedValue) === this.engine.keyOf(next)) {
        return;
      }
      this.appliedValue = next;
      this.changeDetectorRef.markForCheck();
    };
    this.engine.configure(this.source());
  }

  public ngOnChanges(changes: SimpleChanges): void {
    const sourceChanged = ['customDescriptor', 'descriptorApiUrl', 'additionalApiParams', 'noCacheApiDescriptor'].some(
      (key) => changes[key] && !changes[key].isFirstChange()
    );
    if (sourceChanged) {
      this.engine.configure(this.source());
    }
  }

  public ngOnDestroy(): void {
    this.clearPanelScrollTimer();
    this.engine.destroy();
  }

  public onShowPanel = (): void => {
    this.schedulePanelScrollBind(0);
  };

  public onHidePanel = (): void => {
    this.clearPanelScrollTimer();
    this.engine.unbindPanelScroll();
  };

  private schedulePanelScrollBind = (attempt: number): void => {
    this.clearPanelScrollTimer();
    this.panelScrollTimer = setTimeout(() => {
      const element = this.panelScrollElement();
      if (!element) {
        if (attempt < PANEL_SCROLL_BIND_ATTEMPTS) {
          this.schedulePanelScrollBind(attempt + 1);
        }
        return;
      }
      this.engine.bindPanelScroll(element);
    }, PANEL_OPEN_DELAY);
  };

  private panelScrollElement = (): HTMLElement | null => {
    const overlay = this.singleSelectComponent?.dropdown?.overlayViewChild?.overlayViewChild?.nativeElement as HTMLElement | undefined;
    if (!overlay) {
      return null;
    }
    if (this.virtualScroll) {
      return overlay.querySelector('.p-scroller');
    }
    return overlay.querySelector('.p-dropdown-items-wrapper');
  };

  private clearPanelScrollTimer = (): void => {
    if (this.panelScrollTimer) {
      clearTimeout(this.panelScrollTimer);
      this.panelScrollTimer = undefined;
    }
  };

  public search = (filter: string): void => {
    this.filterValue = filter;
    this.changeDetectorRef.markForCheck();
    this.engine.search(filter);
  };

  public onSelect = (value: T | null): void => {
    if (isNil(value) && this.engine.shouldIgnoreClearedSelection) {
      return;
    }
    if (this.engine.matchesResolvedKeys(isNil(value) ? [] : [value])) {
      this.appliedValue = value ?? undefined;
      return;
    }

    this.appliedValue = value ?? undefined;
    this.selectedValueChange.emit(value);
    const key = this.engine.keyOf(value);
    this.selectedKeyChange.emit(isNil(key) ? null : key);
    if (!isNil(value)) {
      this.engine.ensurePresent([value]);
      this.engine.preloadMissing([value]);
    }
  };

  private source = () => ({
    customDescriptor: this.customDescriptor,
    descriptorApiUrl: this.descriptorApiUrl,
    noCacheApiDescriptor: this.noCacheApiDescriptor,
    additionalApiParams: this.additionalApiParams
  });
}
